import { db } from '../firebase';
import {
    doc,
    getDoc,
    updateDoc,
    setDoc
} from 'firebase/firestore';

const DOC_PATH = ["cms", "portfolio_v1"];

// Helper to fetch the full portfolio object
const getPortfolioDoc = async () => {
    const docRef = doc(db, ...DOC_PATH);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error("Portfolio Document not found!");
    return { ref: docRef, data: docSnap.data() };
};

export const fetchCategories = async () => {
    // Structural categories (IDs) are still mostly static or come from the doc.
    // We'll return the hardcoded list for UI consistency, 
    // but we could also fetch them from the doc if we wanted dynamic categories.
    return [
        { id: 'playstore', title: 'App Development' },
        { id: 'python_ai', title: 'Python & AI' },
        { id: 'web', title: 'Web Development' },
        { id: 'research', title: 'Research Papers' },
        { id: 'extensions', title: 'Extensions & Packages' },
        { id: 'activities', title: 'Field Operations' }
    ];
};

export const fetchItems = async (categoryId) => {
    try {
        const { data } = await getPortfolioDoc();
        const category = data.categories.find(c => c.id === categoryId);
        return category ? (category.items || []) : [];
    } catch (error) {
        console.error("Error fetching items:", error);
        return [];
    }
};

export const addItem = async (categoryId, itemData) => {
    try {
        const { ref, data } = await getPortfolioDoc();
        const categories = [...data.categories];
        const catIndex = categories.findIndex(c => c.id === categoryId);

        if (catIndex === -1) throw new Error("Category not found in schema");

        // Generate a pseudo-ID if not present (using timestamp for simplicity in array)
        const newItem = {
            id: itemData.id || `item_${Date.now()}`,
            ...itemData
        };

        if (!categories[catIndex].items) categories[catIndex].items = [];
        categories[catIndex].items.push(newItem);

        await updateDoc(ref, { categories });
        return newItem;
    } catch (error) {
        console.error("Error adding item:", error);
        throw error;
    }
};

export const updateItem = async (categoryId, itemId, itemData) => {
    try {
        const { ref, data } = await getPortfolioDoc();
        const categories = [...data.categories];
        const catIndex = categories.findIndex(c => c.id === categoryId);

        if (catIndex === -1) throw new Error("Category not found");

        const items = categories[catIndex].items || [];
        const itemIndex = items.findIndex(i => i.id === itemId);

        if (itemIndex === -1) throw new Error("Item not found");

        // Update
        items[itemIndex] = { ...items[itemIndex], ...itemData };
        categories[catIndex].items = items;

        await updateDoc(ref, { categories });
        return items[itemIndex];
    } catch (error) {
        console.error("Error updating item:", error);
        throw error;
    }
};

export const deleteItem = async (categoryId, itemId) => {
    try {
        const { ref, data } = await getPortfolioDoc();
        const categories = [...data.categories];
        const catIndex = categories.findIndex(c => c.id === categoryId);

        if (catIndex === -1) throw new Error("Category not found");

        const items = categories[catIndex].items || [];
        const newItems = items.filter(i => i.id !== itemId);
        categories[catIndex].items = newItems;

        await updateDoc(ref, { categories });
        return itemId;
    } catch (error) {
        console.error("Error deleting item:", error);
        throw error;
    }
};

export const migrateFromStaticData = async (staticData) => {
    console.log("Overwriting cms/portfolio_v1 with static data...");
    try {
        const docRef = doc(db, ...DOC_PATH);
        await setDoc(docRef, staticData);
        return { success: 1, failed: 0 };
    } catch (e) {
        console.error("Migration failed", e);
        throw e;
    }
};
